import type { Column } from 'react-table';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { formatDuration, formatTime } from 'shared/utils/formatting';
import { DateTime } from 'luxon';
import SortIcon from 'assets/img/icons/caret-down.svg?react';
import { LevelTableHeaderProps, LwButton, TableCell, TableHeader } from 'redesign';
import { TFunctionNonStrict } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  CheckoutsCellProp,
  CheckoutsColumnBase,
  CheckoutsTableOptions,
} from './get-checkouts-column-definition-base.types';
import { getShiftDurationInMs } from '../utils/get-shift-duration-in-ms';
import { getExpenseWithNameAndValue } from '../../../../../../../shared/utils/expenses';

export const getCheckoutsColumnDefinitionBase: (
  allowExpenses: boolean,
  t: TFunctionNonStrict<'ogp-screen-specific', undefined>
) => Record<
  | 'fw'
  | 'job'
  | 'date'
  | 'scheduled'
  | 'timeDeviationAndBreak'
  | 'expense'
  | 'department'
  | 'branchOffice',
  Column<CheckoutsColumnBase>
> = (allowExpenses, t) => {
  return {
    fw: {
      Header: () => (
        <Typography variant="body2" color="secondary">
          {t('administration.shifts.table_headers.worker', { ns: 'ogp-screen-specific' })}
        </Typography>
      ),
      accessor: 'worker',
      Cell: WorkerCell,
    },
    job: {
      Header: JobHeaderCell,
      accessor: 'job',
      Cell: JobCell,
    },
    scheduled: {
      Header: () => (
        <>
          <Typography variant="body2" color="secondary">
            {t('administration.shifts.table_headers.scheduled', { ns: 'ogp-screen-specific' })}
          </Typography>
          <Typography variant="body2" color="secondary">
            {t('administration.shifts.table_headers.planned_hours', { ns: 'ogp-screen-specific' })}
          </Typography>
        </>
      ),
      accessor: 'scheduledTime',
      Cell: ScheduledTimesCell,
    },
    timeDeviationAndBreak: {
      accessor: 'scheduledVsWorkedDiffAndBreak',
      Header: () => (
        <>
          <Typography variant="body2" color="secondary">
            {t('administration.shifts.table_headers.deviation', { ns: 'ogp-screen-specific' })}
          </Typography>
          <Typography variant="body2" color="secondary">
            {t('administration.shifts.table_headers.break', { ns: 'ogp-screen-specific' })}
          </Typography>
        </>
      ),
      Cell: TimeDeviationBreakCell,
    },
    date: {
      Header: DateHeaderCell,
      accessor: 'date',
      Cell: DateCell,
    },
    expense: {
      Header: () => (
        <TableHeader>
          {t('administration.shifts.table_headers.expenses', { ns: 'ogp-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'expense',
      Cell: (cell: CheckoutsCellProp<'expense'>) => (
        <TableCell>
          {cell.value?.length && (allowExpenses || cell.value.some((i) => i.amount !== 0))
            ? cell.value.map((expense) => (
                <Box key={expense.expenseType}>{getExpenseWithNameAndValue(expense)}</Box>
              ))
            : 'Geen'}
        </TableCell>
      ),
    },
    branchOffice: {
      Header: () => (
        <TableHeader>
          {t('administration.shifts.table_headers.branch_office', { ns: 'ogp-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'branchOffice',
      Cell: (cell: CheckoutsCellProp<'branchOffice'>) => <Typography>{cell.value}</Typography>,
    },
    department: {
      Header: () => (
        <TableHeader>
          {t('administration.shifts.table_headers.department', { ns: 'ogp-screen-specific' })}
        </TableHeader>
      ),
      accessor: 'department',
      Cell: DepartmentCell,
    },
  };
};

const StyledSortIcon = styled(SortIcon)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  height: '12px',
  width: '12px',
  transition: 'ease transform .5s',
}));

const WorkerCell = (cell: CheckoutsCellProp<'worker'>) => {
  const { partnerName } = cell.value;
  if (partnerName) {
    return (
      <>
        <Typography variant="body1">{cell.value.fullName}</Typography>
        <Tooltip title={partnerName}>
          <LabelBox>{partnerName}</LabelBox>
        </Tooltip>
      </>
    );
  }
  return <Typography variant="body1">{cell.value.fullName}</Typography>;
};
const DepartmentCell = (cell: CheckoutsCellProp<'department'>) => {
  return <Typography>{cell.value}</Typography>;
};

const JobHeaderCell = (cell: LevelTableHeaderProps<CheckoutsColumnBase, CheckoutsTableOptions>) => {
  const { t } = useTranslation('ogp-screen-specific');
  return (
    <LwButton
      color="secondary"
      onClick={() => {
        cell.onSortClick('job');
      }}
    >
      <Typography variant="body2" color="secondary">
        {t('administration.shifts.table_headers.job_title', { ns: 'ogp-screen-specific' })}
      </Typography>
      <StyledSortIcon
        style={{
          transform: cell.sortState.order === 'asc' ? 'rotate(180deg)' : '',
          visibility: cell.sortState.sort === 'job' ? 'visible' : 'hidden',
        }}
      />
    </LwButton>
  );
};

const JobCell = (cell: CheckoutsCellProp<'job'>) => {
  return (
    <>
      <Typography variant="body1">{cell.value.name}</Typography>
      <Typography variant="body2" color="secondary">
        {cell.value.location.address}
      </Typography>
    </>
  );
};

const DateHeaderCell = (
  cell: LevelTableHeaderProps<CheckoutsColumnBase, CheckoutsTableOptions>
) => {
  const { t } = useTranslation('ogp-screen-specific');
  return (
    <LwButton
      color="secondary"
      onClick={() => {
        cell.onSortClick('date');
      }}
    >
      <Typography variant="body2" color="secondary">
        {t('administration.shifts.table_headers.date', { ns: 'ogp-screen-specific' })}
      </Typography>
      <StyledSortIcon
        style={{
          transform: cell.sortState.order === 'asc' ? 'rotate(180deg)' : '',
          visibility: cell.sortState.sort === 'date' ? 'visible' : 'hidden',
        }}
      />
    </LwButton>
  );
};

const DateCell = (cell: CheckoutsCellProp<'date'>) => {
  const { scheduledStartDate, timeZone } = cell.value;
  return (
    <Typography variant="body1">
      {DateTime.fromJSDate(scheduledStartDate, { zone: timeZone })
        .toFormat('EEE, dd MMM yyyy')
        .replace(/\./g, '')
        .replace(/^\w/, (w) => w.toUpperCase())}
    </Typography>
  );
};

export const ScheduledTimesCell = (cell: CheckoutsCellProp<'scheduledTime'>) => {
  const { startDate, endDate, timeZone, breakInMinutes } = cell.value;
  const scheduledDurationMs = getShiftDurationInMs(startDate, endDate, breakInMinutes);
  const scheduledTimeRange = `${formatTime(startDate, timeZone)} - ${formatTime(
    endDate,
    timeZone
  )}`;

  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="body2">{scheduledTimeRange}</Typography>
      <Typography variant="body2">
        {formatDuration({ milliseconds: scheduledDurationMs })}
      </Typography>
    </Box>
  );
};

const TimeDeviationBreakCell = (cell: CheckoutsCellProp<'scheduledVsWorkedDiffAndBreak'>) => {
  const { deviationInMs, breakInMinutes } = cell.value;

  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="body2">{formatDuration({ milliseconds: deviationInMs })}</Typography>
      <Typography variant="body2">{formatDuration({ minutes: breakInMinutes })}</Typography>
    </Box>
  );
};

const LabelBox = styled(Box)(() => ({
  border: '1px solid #eeeeee',
  borderRadius: '16px',
  backgroundColor: 'white',
  width: '100px',
  padding: '2px 8px',
  marginTop: '0px',
  lineHeight: '12px',
  fontSize: '11px',
  textAlign: 'center',
  zIndex: 100,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
}));
